import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import { ArrowUp, ArrowDown, ChevronsUpDown } from 'lucide-react'
import LoadingSpinner from '../../components/LoadingSpinner'
import ErrorMessage from '../../components/ErrorMessage'
import ExportButtons from '../../components/ExportButtons'
import type { ColumnDefinition } from '../../utils/exportToCsv'
import { useDealers } from './hooks/useDealers'
import { useDealersCrossBank } from './hooks/useDealersCrossBank'
import type { BruktbilfinansieringFilters } from './hooks/useBruktbilfinansiering'
import type { CrossBankDealerRow, DealerRow } from '../../types/bruktbilfinansiering'
import BruktbilfinansieringInfoBox from './BruktbilfinansieringInfoBox'
import { useMineMarkedsomrader } from '../../hooks/useMineMarkedsomrader'
import { useActiveMarkedsomrade } from '../../hooks/useActiveMarkedsomrade'
interface DealersTabProps {
  bankOrgNr: string
  bankName: string
  filters: BruktbilfinansieringFilters
  sortBy: string
  sortDir: 'asc' | 'desc'
  onSortChange: (col: string, dir: 'asc' | 'desc') => void
}

const _nb = new Intl.NumberFormat('nb-NO')

function formatShare(v: number): string { return _nb.format(+(v * 100).toFixed(1)) + ' %' }

function shareColor(share: number): string {
  if (share >= 0.4) return 'text-green-600 dark:text-green-400'
  if (share >= 0.2) return 'text-yellow-600 dark:text-yellow-400'
  return 'text-red-600 dark:text-red-400'
}

const PAGE_SIZE = 50

const exportColumns = (t: TFunction<'bbf'>): ColumnDefinition[] => [
  { key: 'dealer_org_nr', label: t('dealers.export.dealerOrgNr') },
  { key: 'dealer_name', label: t('shared.dealer') },
  { key: 'dealer_home_postnummer', label: t('dealers.export.postnummer'), format: (v) => v ?? '' },
  { key: 'dealer_home_kommune', label: t('shared.kommune'), format: (v) => v ?? '' },
  { key: 'dealer_home_fylke', label: t('dealers.export.fylke'), format: (v) => v ?? '' },
  { key: 'total_all_sales', label: t('dealers.export.totalAllSales') },
  { key: 'total_in_scope_sales', label: t('dealers.export.inScopeSales') },
  { key: 'total_loans_all_banks', label: t('dealers.export.loanShareAllBanks'), format: (_v, row: DealerRow) =>
      row.total_in_scope_sales > 0 ? (row.total_loans_all_banks / row.total_in_scope_sales * 100).toFixed(1) : '' },
  { key: 'bank_financed_count', label: t('dealers.export.bankFinanced') },
  { key: 'total_loans_all_banks', label: t('dealers.export.totalLoans') },
  { key: 'bank_market_share', label: t('dealers.export.marketShareOwn'), format: (v) => v != null ? (Number(v) * 100).toFixed(1) : '' },
  { key: 'bank_total_nok', label: t('dealers.export.volumeOwn'), format: (v) => v != null ? Number(v).toFixed(0) : '' },
  { key: 'total_volume_all_banks', label: t('dealers.export.volumeAll'), format: (v) => v != null ? Number(v).toFixed(0) : '' },
  { key: 'top_competing_bank', label: t('dealers.export.topCompetitor'), format: (_v, row: DealerRow) => row?.top_competing_bank?.name ?? '' },
  { key: 'top_competing_bank', label: t('dealers.export.topCompetitorShare'), format: (_v, row: DealerRow) => row?.top_competing_bank ? (row.top_competing_bank.share * 100).toFixed(1) : '' },
]

const BANK_CODES = ['DNB', 'SAN', 'NOR', 'VWF', 'TFS', 'BMW', 'BRA', 'FIN', 'SPN', 'SPO', 'SPM', 'AND'] as const

const BANK_FULL_NAMES: Record<string, string> = {
  DNB: 'DNB Bank / DNB Finans',
  SAN: 'Santander Consumer Bank',
  NOR: 'Nordea Finans',
  VWF: 'Volkswagen Møller Bilfinans',
  TFS: 'Toyota Kreditbank',
  BMW: 'BMW Financial Services',
  BRA: 'Brage Finans',
  FIN: 'AS Financiering',
  SPN: 'Sparebank 1 Finans Nord-Norge',
  SPO: 'Sparebank 1 Finans Østlandet',
  SPM: 'Sparebank 1 Finans Midt-Norge',
}

function BankBadges({ row }: { row: CrossBankDealerRow }) {
  const { t } = useTranslation('bbf')
  if (row.laan === 0) return <span className="text-gray-400">—</span>
  const entries = BANK_CODES
    .map(code => ({ code, pct: (row as unknown as Record<string, number>)[code] ?? 0 }))
    .filter(e => e.pct > 0)
    .sort((a, b) => b.pct - a.pct)
    .slice(0, 3)
  return (
    <div className="flex gap-1 flex-nowrap">
      {entries.map(e => (
        <span
          key={e.code}
          title={e.code === 'AND' ? t('dealers.otherBanks') : (BANK_FULL_NAMES[e.code] ?? e.code)}
          className="text-xs px-1.5 py-0.5 rounded-sm bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 cursor-help whitespace-nowrap"
        >
          {e.code} {e.pct.toLocaleString('nb-NO', { maximumFractionDigits: 0 })}%
        </span>
      ))}
    </div>
  )
}

function buildDealerDetailsUrl(bankOrgNr: string, dealerOrgNr: string, filters: BruktbilfinansieringFilters, markedsomradeName?: string | null): string {
  const sp = new URLSearchParams()
  sp.set('start_date', filters.startDate)
  sp.set('end_date', filters.endDate)
  if (filters.varebil !== undefined) sp.set('varebil', String(filters.varebil))
  // Carry the grid's geo selection so the detail page inherits it (issue #301)
  if (filters.dealerPostnummer) sp.set('dealer_postnummer', filters.dealerPostnummer)
  if (filters.buyerPostnummer) sp.set('buyer_postnummer', filters.buyerPostnummer)
  // Pass markedsomrade name so the detail page can display it
  if (markedsomradeName) sp.set('markedsomrade_name', markedsomradeName)
  // Carry buyer-type classes so the detail page inherits the filter
  if (filters.classes?.length) {
    filters.classes.forEach(c => sp.append('classes', c))
  }
  return `/bruktbil-finansiering/${bankOrgNr}/dealer/${dealerOrgNr}?${sp.toString()}`
}

export default function DealersTab({ bankOrgNr, bankName, filters, sortBy, sortDir, onSortChange }: DealersTabProps) {
  const { t } = useTranslation('bbf')
  const { data, loading, error } = useDealers(bankOrgNr, filters, sortBy, sortDir)
  const { data: crossBankData } = useDealersCrossBank(bankOrgNr, filters)
  const navigate = useNavigate()

  const { items: omrader } = useMineMarkedsomrader()
  const { activeId } = useActiveMarkedsomrade()
  const activeOmrade = activeId != null ? omrader.find(o => o.id === activeId) : undefined
  const geoFilterActive = !!(filters.buyerPostnummer || filters.dealerPostnummer)
  const markedsomradeName = geoFilterActive ? (activeOmrade?.name ?? null) : null

  const [page, setPage] = useState(1)
  const [searchQuery, setSearchQuery] = useState('')
  const classesKey = filters.classes?.join('|') ?? ''
  useEffect(() => { setPage(1) }, [bankOrgNr, filters.startDate, filters.endDate, filters.buyerPostnummer, filters.varebil, classesKey, sortBy, sortDir])
  useEffect(() => { setSearchQuery('') }, [bankOrgNr, filters.startDate, filters.endDate, filters.buyerPostnummer, filters.varebil, classesKey])

  // Build a lookup map from cross-bank data for bank badges
  const crossBankMap = new Map<string, CrossBankDealerRow>()
  if (crossBankData?.data) {
    for (const row of crossBankData.data) {
      crossBankMap.set(row.forhandler_org_nr, row)
    }
  }

  const SortIcon = ({ col }: { col: string }) => {
    if (sortBy !== col) return <ChevronsUpDown className="h-4 w-4 text-gray-400 inline ml-1" />
    if (sortDir === 'asc') return <ArrowUp className="h-4 w-4 inline ml-1" />
    return <ArrowDown className="h-4 w-4 inline ml-1" />
  }

  const handleSort = (col: string) => {
    if (sortBy === col) {
      onSortChange(col, sortDir === 'asc' ? 'desc' : 'asc')
    } else {
      onSortChange(col, 'desc')
    }
  }

  if (loading) return <LoadingSpinner />
  if (error) return <ErrorMessage message={t('dealers.loadError')} />

  const allRows = data?.data ?? []

  const filteredRows = searchQuery.trim()
    ? allRows.filter(r => {
        const q = searchQuery.toLowerCase()
        return r.dealer_name.toLowerCase().includes(q) || r.dealer_org_nr.includes(q)
      })
    : allRows

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / PAGE_SIZE))
  const safePage = Math.min(page, totalPages)
  const rows = filteredRows.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE)

  const totals = filteredRows.reduce(
    (acc, r) => ({
      total_all_sales: acc.total_all_sales + Number(r.total_all_sales),
      total_in_scope_sales: acc.total_in_scope_sales + Number(r.total_in_scope_sales),
      bank_financed_count: acc.bank_financed_count + Number(r.bank_financed_count),
      total_loans_all_banks: acc.total_loans_all_banks + Number(r.total_loans_all_banks),
      bank_total_nok: acc.bank_total_nok + Number(r.bank_total_nok),
      total_volume_all_banks: acc.total_volume_all_banks + Number(r.total_volume_all_banks),
    }),
    { total_all_sales: 0, total_in_scope_sales: 0, bank_financed_count: 0,
      total_loans_all_banks: 0, bank_total_nok: 0, total_volume_all_banks: 0 }
  )
  const totalsMarketShare = totals.total_loans_all_banks > 0
    ? totals.bank_financed_count / totals.total_loans_all_banks
    : 0
  const totalsLoanShare = totals.total_in_scope_sales > 0
    ? totals.total_loans_all_banks / totals.total_in_scope_sales
    : null

  return (
    <div className="space-y-4">
      <BruktbilfinansieringInfoBox bankName={bankName} filters={filters} tab="forhandlere" />
      <div className="flex justify-end">
        <ExportButtons
          data={allRows}
          columns={exportColumns(t)}
          metadata={{ title: t('dealers.export.title') }}
          disabled={loading}
        />
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <input
            type="text"
            value={searchQuery}
            onChange={e => { setSearchQuery(e.target.value); setPage(1) }}
            placeholder={t('dealers.searchPlaceholder')}
            className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-hidden focus:ring-1 focus:ring-primary-400 w-72"
          />
          {searchQuery.trim() && (
            <span className="text-sm text-gray-500 dark:text-gray-400">
              {t('dealers.searchCount', { shown: filteredRows.length, total: allRows.length })}
            </span>
          )}
        </div>
      </div>

      {allRows.length === 0 ? (
        <p className="text-gray-500 dark:text-gray-400 text-center py-8">
          {t('dealers.empty')}
        </p>
      ) : (
        <>
        <div className="overflow-x-auto card p-0">
          <table className="data-table data-table-compact">
            <thead>
              <tr className="text-left">
                <th className="cursor-pointer whitespace-nowrap w-full" onClick={() => handleSort('dealer_name')}>
                  {t('shared.dealer')} <SortIcon col="dealer_name" />
                </th>
                <th>{t('dealers.cols.postnr')}</th>
                <th>{t('shared.kommune')}</th>
                <th className="cursor-pointer whitespace-nowrap text-right" title={t('dealers.cols.totalCarsTitle')} onClick={() => handleSort('total_all_sales')}>
                  {t('dealers.cols.totalCars')} <SortIcon col="total_all_sales" />
                </th>
                <th className="cursor-pointer whitespace-nowrap text-right" title={t('dealers.cols.carsInScopeTitle')} onClick={() => handleSort('total_in_scope_sales')}>
                  {t('dealers.cols.carsInScope')} <SortIcon col="total_in_scope_sales" />
                </th>
                <th className="whitespace-nowrap text-right" title={t('dealers.cols.loanShareTitle')}>
                  {t('dealers.cols.loanShare')}
                </th>
                <th className="cursor-pointer whitespace-nowrap text-right" title={t('dealers.cols.bankLoansTitle')} onClick={() => handleSort('bank_financed_count')}>
                  {t('dealers.cols.bankLoans')} <SortIcon col="bank_financed_count" />
                </th>
                <th className="cursor-pointer whitespace-nowrap text-right" title={t('dealers.cols.totalLoansTitle')} onClick={() => handleSort('total_loans_all_banks')}>
                  {t('dealers.cols.totalLoans')} <SortIcon col="total_loans_all_banks" />
                </th>
                <th className="cursor-pointer whitespace-nowrap text-right" title={t('dealers.cols.marketShareTitle')} onClick={() => handleSort('bank_market_share')}>
                  {t('dealers.cols.marketShare')} <SortIcon col="bank_market_share" />
                </th>
                <th className="text-right whitespace-nowrap" title={t('dealers.cols.volumeBankTitle')}>{t('dealers.cols.volumeBank')}</th>
                <th className="text-right whitespace-nowrap" title={t('dealers.cols.volumeBanksTitle')}>{t('dealers.cols.volumeBanks')}</th>
                <th className="min-w-44" title={t('dealers.cols.bankmixTitle')}>{t('dealers.cols.bankmix')}</th>
              </tr>
            </thead>
            <tbody data-testid="dealers-grid-body">
              <tr
                data-testid="dealers-total-row"
                className="bg-primary-50 dark:bg-primary-900/20 font-bold border-t border-b border-primary-300 dark:border-primary-600"
              >
                <td className="font-bold text-primary-700 dark:text-primary-300 py-2">
                  <span className="text-primary-600 dark:text-primary-400 font-bold mr-1">∑</span>
                  TOTAL
                </td>
                <td className="py-2"></td>
                <td className="py-2"></td>
                <td className="text-right tabular-nums py-2">{_nb.format(totals.total_all_sales)}</td>
                <td className="text-right tabular-nums py-2">{_nb.format(totals.total_in_scope_sales)}</td>
                <td className="text-right tabular-nums py-2">
                  {totalsLoanShare !== null
                    ? _nb.format(+(totalsLoanShare * 100).toFixed(1))
                    : '—'}
                </td>
                <td className="text-right tabular-nums py-2">{_nb.format(totals.bank_financed_count)}</td>
                <td className="text-right tabular-nums py-2">{_nb.format(totals.total_loans_all_banks)}</td>
                <td className={`text-right tabular-nums py-2 ${shareColor(totalsMarketShare)}`}>
                  {_nb.format(+(totalsMarketShare * 100).toFixed(1))}
                </td>
                <td className="text-right tabular-nums py-2">{_nb.format(totals.bank_total_nok)}</td>
                <td className="text-right tabular-nums py-2">{_nb.format(totals.total_volume_all_banks)}</td>
                <td className="py-2 text-gray-400">—</td>
              </tr>
              {rows.map((row, idx) => {
                const detailsUrl = buildDealerDetailsUrl(bankOrgNr, row.dealer_org_nr, filters, markedsomradeName)
                return (
                  <tr
                    key={row.dealer_org_nr}
                    className={`${idx % 2 === 0 ? 'bg-gray-50 dark:bg-gray-800/30' : ''} hover:bg-gray-100 dark:hover:bg-dark-border transition-colors cursor-pointer`}
                    onClick={() => navigate(detailsUrl)}
                  >
                    <td className="font-medium">
                      <Link
                        to={detailsUrl}
                        className="text-primary-600 dark:text-primary-400 hover:underline"
                        onClick={e => e.stopPropagation()}
                      >
                        {row.dealer_name}
                      </Link>
                    </td>
                    <td className="text-gray-500">{row.dealer_home_postnummer ?? '—'}</td>
                    <td className="text-gray-500">{row.dealer_home_kommune ?? '—'}</td>
                    <td className="text-right tabular-nums">{_nb.format(row.total_all_sales)}</td>
                    <td className="text-right tabular-nums">{_nb.format(row.total_in_scope_sales)}</td>
                    <td className="text-right tabular-nums">
                      {row.total_in_scope_sales > 0
                        ? _nb.format(+(row.total_loans_all_banks / row.total_in_scope_sales * 100).toFixed(1))
                        : '—'}
                    </td>
                    <td className="text-right tabular-nums">{_nb.format(row.bank_financed_count)}</td>
                    <td className="text-right tabular-nums">{_nb.format(row.total_loans_all_banks)}</td>
                    <td className={`text-right font-medium tabular-nums ${shareColor(row.bank_market_share)}`}>
                      {_nb.format(+(row.bank_market_share * 100).toFixed(1))}
                    </td>
                    <td className="text-right tabular-nums">{_nb.format(row.bank_total_nok)}</td>
                    <td className="text-right tabular-nums">{_nb.format(row.total_volume_all_banks)}</td>
                    <td>
                      {row.total_loans_all_banks > row.bank_financed_count
                        ? crossBankMap.has(row.dealer_org_nr)
                          ? <BankBadges row={crossBankMap.get(row.dealer_org_nr)!} />
                          : row.top_competing_bank
                          ? <span className="text-gray-500 text-xs">{row.top_competing_bank.name} ({formatShare(row.top_competing_bank.share)})</span>
                          : <span className="text-gray-400">—</span>
                        : <span className="text-gray-400">—</span>}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="flex items-center justify-between text-sm text-gray-600 dark:text-gray-400">
            <span>
              {t('shared.pagination', { page: safePage, total: totalPages, from: (safePage - 1) * PAGE_SIZE + 1, to: Math.min(safePage * PAGE_SIZE, filteredRows.length), rows: filteredRows.length })}
            </span>
            <div className="flex gap-2">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={safePage === 1}
                className="px-3 py-1 rounded-sm border border-gray-300 dark:border-gray-600 disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                {t('shared.prev')}
              </button>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={safePage === totalPages}
                className="px-3 py-1 rounded-sm border border-gray-300 dark:border-gray-600 disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                {t('shared.next')}
              </button>
            </div>
          </div>
        )}
        </>
      )}
    </div>
  )
}
